import { useMemo } from "react";

import {
  DISABLED_LOGO,
  DISABLED_MESSAGES,
  searchIsEnabled,
} from "../../constants/command-palette.constants";
import { TRENDING_TOPICS } from "../../content/trending-topics";
import { sendCustomGAEvent } from "../../utils/google-event";

type Props = {
  isLoading: boolean;
  hasOptions: boolean;
  query: string;
};

/**
 * This component is used to display the correct alternative view based on the state of the search.
 */
export const CommandPaletteAltStates = ({
  isLoading,
  hasOptions,
  query,
}: Props) => {
  const isDisabled = !searchIsEnabled(query);

  if (isLoading) {
    return (
      <div
        className="plh-flex plh-items-center plh-justify-center plh-pt-[60px]"
        data-testid="loading-state"
      >
        <SyncLoader />
      </div>
    );
  }

  if (!isLoading && !hasOptions && isDisabled) {
    return <CommandPaletteDisabledCTAMessage />;
  }

  if (!isLoading && !hasOptions && !isDisabled) {
    return <CommandPaletteNoResults query={query} />;
  }

  return null;
};

/**
 * This component is used to display a message when the search is disabled.
 */
const CommandPaletteDisabledCTAMessage = () => {
  const startTypingMessage = useMemo(() => {
    return DISABLED_MESSAGES[
      Math.floor(Math.random() * DISABLED_MESSAGES.length)
    ];
  }, []);
  const trackTrendsClick = (title: string) => {
    sendCustomGAEvent('click', {
      event_category: 'trends_com_pal',
      event_label: "trends_com_pal_click",
      value: title,
    });
  }

  return (
    <div
      className="plh-mx-auto plh-max-w-[401px] plh-px-2 plh-py-10"
      data-testid="disabled-state"
    >
      <img
        className="plh-mx-auto plh-size-[80px] plh-mb-7"
        src={DISABLED_LOGO}
        alt=""
      />
      <p className="plh-mb-7 plh-text-center plh-font-quicksand plh-text-h5 plh-text-grey-2">
        {startTypingMessage}
      </p>
      {TRENDING_TOPICS.map((topic) => (
        <div className="plh-text-center plh-my-1" key={topic.title}>
          <a className="plh-text-primary-blue-2 plh-text-link plh-font-roboto" href={topic.link} 
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              trackTrendsClick(topic.title);
              window.location.href = topic.link;
            }
          }} 
          onClick={() => {
            trackTrendsClick(topic.title);
          }}>{topic.title}</a>
        </div>
      ))}
    </div>
  );
};

/**
 * This component is used to display a message when no results are found.
 */
const CommandPaletteNoResults = ({ query }: { query: string }) => {
  return (
    <div
      className="plh-mx-auto plh-max-w-[401px] plh-px-2 plh-py-10"
      data-testid="no-results-state"
    >
      <p className="plh-pb-6 plh-text-center plh-font-quicksand plh-text-h5 plh-text-grey-2">
        No results found for "<span className="plh-break-words">{query}</span>
        ".
      </p>
      <img
        className="plh-mx-auto plh-size-[80px]"
        src="https://assets.cloud.pennylane.ai/command_palette/icons/no_results.png"
        alt=""
      />
    </div>
  );
};

export const SyncLoader: React.FC = () => {
  return (
    <div className="sync-loader">
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
    </div>
  );
};

export default SyncLoader;
